import { Timestamp } from 'firebase/firestore';

// ============================================
// WORKSHEET ITEMS (Tételek)
// ============================================

export type WorksheetItemType = 'material' | 'labor' | 'travel' | 'other' | 'header';

export interface WorksheetItem {
  id: string;
  type: WorksheetItemType;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  discount?: number;
  discountType?: 'percent' | 'fixed';
  warranty?: string;
  notes?: string;
  equipmentId?: string;
  totalNet: number;
  totalGross: number;
  order: number;
  productId?: string;
  stockDeducted?: boolean;
}

// ============================================
// STATUS HISTORY
// ============================================

export type WorksheetStatus =
  | 'draft'
  | 'assigned'
  | 'in_progress'
  | 'review_pending'
  | 'completed'
  | 'sent'
  | 'partially_invoiced'
  | 'invoiced'
  | 'cancelled'
  | 'hibernated';

export type ServiceStatus =
  | 'received'
  | 'diagnosis'
  | 'awaiting_parts'
  | 'repairing'
  | 'testing'
  | 'ready_for_pickup'
  | 'handed_over';

export interface WorksheetStatusChange {
  id: string;
  fromStatus: WorksheetStatus | ServiceStatus;
  toStatus: WorksheetStatus | ServiceStatus;
  changedBy: string;
  changedByName?: string;
  changedAt: Timestamp | Date;
  reason?: string;
  notes?: string;
}

// ============================================
// TIME TRACKING
// ============================================

export interface TimeEntry {
  id: string;
  worksheetId: string;
  taskId?: string;
  userId: string;
  userName?: string;
  startTime: Timestamp | Date;
  endTime?: Timestamp | Date;
  duration: number;
  description?: string;
  workerCount?: number;
  gpsLocation?: { lat: number; lng: number };
  isManual?: boolean;
  createdAt: Timestamp | Date;
}

// ============================================
// EQUIPMENT (Eszközadatok - szerviz mód)
// ============================================

export interface WorksheetEquipment {
  id: string;
  type?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  accessories?: string;
  reportedIssue?: string;
  storageLocation?: string;
  condition?: string;
  notes?: string;
}

// ============================================
// CUSTOM FIELDS (Egyedi mezők)
// ============================================

export type CustomFieldType = 'text' | 'number' | 'date' | 'datetime';

export interface WorksheetCustomField {
  id: string;
  name: string;
  type: CustomFieldType;
  required: boolean;
  showInList: boolean;
  showInPdf: boolean;
  organizationId: string;
  createdAt: Date;
}

export interface WorksheetCustomFieldValue {
  fieldId: string;
  value: string;
}

// ============================================
// CUSTOM STATUSES (Saját státuszok)
// ============================================

export interface WorksheetCustomStatus {
  id: string;
  name: string;
  color: string;
  countsAsCompleted: boolean;
  triggerStockDeduction: boolean;
  lockEditing: boolean;
  notifyCustomer: boolean;
  organizationId: string;
  order: number;
}

// ============================================
// WORKSHEET TEMPLATE
// ============================================

export interface WorksheetTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  defaultItems: Omit<WorksheetItem, 'id' | 'totalNet' | 'totalGross'>[];
  defaultTasks: WorksheetTask[];
  estimatedHours?: number;
  requiredSkills?: string[];
  defaultWorkType?: 'installation' | 'maintenance' | 'repair' | 'inspection' | 'other';
  defaultPriority?: 'low' | 'medium' | 'high';
  organizationId: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// ============================================
// WORKSHEET TASK (feladat checklist)
// ============================================

export interface WorksheetTask {
  id: string;
  title: string;
  description?: string;
  isCompleted: boolean;
  completedAt?: Timestamp | Date;
  completedBy?: string;
  estimatedMinutes?: number;
  actualMinutes?: number;
  order: number;
}

// ============================================
// NOTES
// ============================================

export interface WorksheetNote {
  id: string;
  text: string;
  visibility: 'customer' | 'internal';
  createdBy: string;
  createdByName?: string;
  createdAt: Timestamp | Date;
}

// ============================================
// EXISTING (backward compatible)
// ============================================

export interface WorksheetSignature {
  id: string;
  signedBy: string;
  signerName: string;
  signerRole: string;
  signatureData: string;
  signedAt: Timestamp | Date;
  location?: string;
  deviceInfo?: string;
}

export type WorksheetPhotoPhase = 'before' | 'after' | 'other';
export interface WorksheetPhoto {
  id: string;
  url: string;
  path: string;
  phase: WorksheetPhotoPhase;
  caption?: string;
  width?: number;
  height?: number;
  size?: number;
  takenAt: Timestamp | Date;
  uploadedBy: string;
  uploadedByName?: string;
}

export interface WorksheetDocument {
  id: string;
  name: string;
  originalName?: string;
  url: string;
  uploadedAt: Timestamp | Date;
  type: 'worksheet';
  size?: number;
}

// ============================================
// EXTENDED WORKSHEET
// ============================================

export interface CreateWorksheetData {
  title: string;
  description?: string;
  customerId: string;
  templateId?: string;
  status: WorksheetStatus;
  priority: 'low' | 'medium' | 'high';
  assignedTo?: string;
  
  // Dátumok és időzítés
  dueDate?: Date;
  worksheetDate?: Date;
  startTime?: string;
  endTime?: string;
  
  // Kiszállási adatok
  deploymentOccurred?: boolean;
  deploymentDate?: Date;
  deploymentTime?: string;
  
  // Utazási adatok
  travelKmStart?: number;
  travelKmEnd?: number;
  travelKmTotal?: number;
  travelCosts?: number;
  
  // Munka részletei
  workDetails?: string;
  materials?: string;
  equipment?: string;
  safetyNotes?: string;
  estimatedHours?: number;
  actualHours?: number;
  workType: 'installation' | 'maintenance' | 'repair' | 'inspection' | 'other';
  location?: string;
  
  // Egyéb
  specialNotes?: string;
  organizationId?: string;
  
  // NEW: Extended fields
  mode?: 'field' | 'service' | 'mixed';
  parentWorksheetId?: string;
  isParent?: boolean;
  items?: WorksheetItem[];
  tasks?: WorksheetTask[];
  statusHistory?: WorksheetStatusChange[];
  timeEntries?: TimeEntry[];
  equipmentData?: WorksheetEquipment[];
  notes_list?: WorksheetNote[];
  publicLink?: string;
  publicLinkToken?: string;
  serviceStatus?: ServiceStatus;
  discount?: number;
  discountType?: 'percent' | 'fixed';
  warranty?: string;
  category?: string;
  
  // Custom fields
  customFieldValues?: WorksheetCustomFieldValue[];
  
  // Conversion tracking
  convertedFrom?: { type: 'quotation' | 'survey' | 'worksheet'; id: string; number?: string };
  convertedTo?: { type: 'quotation' | 'worksheet'; id: string; number?: string }[];
  
  // Stock deduction
  stockDeductedStatus?: string;
  stockDeductedAt?: Date;
}

export interface Worksheet extends CreateWorksheetData {
  id: string;
  worksheetNumber: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  signatures?: WorksheetSignature[];
  documents?: WorksheetDocument[];
  photos?: WorksheetPhoto[];
  /** Ügyfél visszajelzés a nyilvános linkről (1–5 csillag) */
  feedback?: { rating: number; comment?: string; name?: string; at?: Date | any; source?: string };
  completedAt?: Date;
  completedBy?: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

export function calculateItemTotal(
  quantity: number,
  unitPrice: number,
  vatRate: number,
  discount?: number,
  discountType?: 'percent' | 'fixed'
): { net: number; gross: number } {
  let net = quantity * unitPrice;
  if (discount && discount > 0) {
    if (discountType === 'percent') {
      net = net * (1 - discount / 100);
    } else {
      net = net - discount;
    }
  }
  const gross = net * (1 + vatRate / 100);
  return { net: Math.round(net * 100) / 100, gross: Math.round(gross * 100) / 100 };
}

export function calculateWorksheetTotals(items: WorksheetItem[]): {
  totalNet: number;
  totalGross: number;
  totalVat: number;
} {
  let totalNet = 0;
  let totalGross = 0;
  for (const item of items) {
    if (item.type === 'header') continue;
    totalNet += item.totalNet;
    totalGross += item.totalGross;
  }
  return {
    totalNet: Math.round(totalNet * 100) / 100,
    totalGross: Math.round(totalGross * 100) / 100,
    totalVat: Math.round((totalGross - totalNet) * 100) / 100,
  };
}

export const WORKSHEET_STATUSES: { value: WorksheetStatus; labelKey: string; color: string }[] = [
  { value: 'draft', labelKey: 'worksheets.status.draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'assigned', labelKey: 'worksheets.status.assigned', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', labelKey: 'worksheets.status.in_progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'review_pending', labelKey: 'worksheets.status.review_pending', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', labelKey: 'worksheets.status.completed', color: 'bg-green-100 text-green-700' },
  { value: 'sent', labelKey: 'worksheets.status.sent', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'partially_invoiced', labelKey: 'worksheets.status.partially_invoiced', color: 'bg-orange-100 text-orange-700' },
  { value: 'invoiced', labelKey: 'worksheets.status.invoiced', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'cancelled', labelKey: 'worksheets.status.cancelled', color: 'bg-red-100 text-red-700' },
  { value: 'hibernated', labelKey: 'worksheets.status.hibernated', color: 'bg-slate-100 text-slate-700' },
];

export const SERVICE_STATUSES: { value: ServiceStatus; label: string; color: string }[] = [
  { value: 'received', label: 'Átvéve', color: 'bg-gray-100 text-gray-700' },
  { value: 'diagnosis', label: 'Diagnosztika', color: 'bg-blue-100 text-blue-700' },
  { value: 'awaiting_parts', label: 'Alkatrészre vár', color: 'bg-amber-100 text-amber-700' },
  { value: 'repairing', label: 'Javítás alatt', color: 'bg-purple-100 text-purple-700' },
  { value: 'testing', label: 'Tesztelés', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'ready_for_pickup', label: 'Kész, átvehető', color: 'bg-green-100 text-green-700' },
  { value: 'handed_over', label: 'Kiadva', color: 'bg-emerald-100 text-emerald-700' },
];

export const ITEM_TYPES: { value: WorksheetItemType; label: string; icon: string }[] = [
  { value: 'material', label: 'Anyag', icon: '📦' },
  { value: 'labor', label: 'Munkadíj', icon: '🔧' },
  { value: 'travel', label: 'Kiszállás', icon: '🚗' },
  { value: 'other', label: 'Egyéb', icon: '📋' },
  { value: 'header', label: 'Címsor', icon: '🏷️' },
];

export const UNITS = [
  'db', 'ó', 'perc', 'km', 'm', 'm2', 'm3', 'ft', 'cm', 'mm',
  'liter', 'kg', 'tonna', 'doboz', 'csomag', 'tekercs', 'lap',
  'méter', 'alkalom', 'nap', 'hét', 'hó', 'év', 'set', 'pár',
  'can', 'flakon', 'zsák', 'raklap', 'konténer', 'szett',
];

export const VAT_RATES = [0, 5, 18, 27]; 